import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { IdentityService } from '../fetcher/IdentityService.js';
import { SessionStorage } from '../fetcher/SessionStorage.js';
import { NOTICES } from './notices.js';

export const NOTICEBOARD_SETTINGS_KEY = 'noticeboard';
export const NOTICEBOARD_SNOOZE_STORAGE_KEY = 'medprompt-noticeboard-snooze';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

export const reminderDelayMs = (notice) => {
  const days = notice?.remindAfterDays;
  if (typeof days !== 'number' || !Number.isFinite(days) || days <= 0) return null;
  return days * MS_PER_DAY;
};

export const normalizeSnoozeFor = (notice) => {
  if (!Array.isArray(notice?.snoozeFor)) return [];
  return notice.snoozeFor.filter((option) => (
    option
    && typeof option.label === 'string'
    && option.label.trim() !== ''
    && isFiniteNumber(option.ms)
    && option.ms > 0
  ));
};

export const defaultSnoozeMs = (notice) => {
  const reminder = reminderDelayMs(notice);
  if (reminder != null) return reminder;
  return normalizeSnoozeFor(notice)[0]?.ms ?? null;
};

export const canRecur = (notice) => (
  reminderDelayMs(notice) != null || normalizeSnoozeFor(notice).length > 0
);

export const isNoticeComplete = (notice, entry) => {
  if (!entry) return false;
  if (!canRecur(notice)) return true;
  if (notice?.interactable) return entry.data !== undefined;
  return false;
};

export const toViewedAtList = (viewedAt) => {
  if (Array.isArray(viewedAt)) {
    return viewedAt.filter(time => typeof time === 'number' && Number.isFinite(time));
  }
  if (typeof viewedAt === 'number' && Number.isFinite(viewedAt)) {
    return [viewedAt];
  }
  return [];
};

export const latestViewedAt = (entry) => {
  const times = toViewedAtList(entry?.viewedAt);
  return times.length === 0 ? 0 : times[times.length - 1];
};

export const isSnoozeNotice = (notice) => normalizeSnoozeFor(notice).length > 0;

export const resolveSnoozedUntil = (notice, entry) => {
  if (isFiniteNumber(entry?.snoozedUntil)) {
    return entry.snoozedUntil;
  }
  if (isSnoozeNotice(notice)) {
    return null;
  }
  const delay = reminderDelayMs(notice);
  const latest = latestViewedAt(entry);
  if (delay != null && latest > 0) {
    return latest + delay;
  }
  return null;
};

export const noticeClinicId = (notice) => {
  const clinicId = notice?.clinicId;
  if (typeof clinicId !== 'string') return null;
  const trimmed = clinicId.trim();
  return trimmed === '' ? null : trimmed;
};

export const isNoticeForClinic = (notice, selectedClinicId) => {
  const clinicId = noticeClinicId(notice);
  if (clinicId == null) return true;
  return clinicId === selectedClinicId;
};

export const isNoticeDue = (notice, entry, now = Date.now()) => {
  if (isNoticeComplete(notice, entry)) return false;
  if (!entry) return true;
  const until = resolveSnoozedUntil(notice, entry);
  if (until != null) return now >= until;
  return canRecur(notice);
};

export const isLoginPresent = (notice, entry, now = Date.now()) => {
  if (!isNoticeDue(notice, entry, now)) return false;
  if (!isSnoozeNotice(notice)) return true;
  const until = resolveSnoozedUntil(notice, entry);
  return until == null || now >= until;
};

export const isSnoozeWakePresent = (notice, entry, now = Date.now()) => {
  if (!isSnoozeNotice(notice) || !isNoticeDue(notice, entry, now)) return false;
  return resolveSnoozedUntil(notice, entry) != null;
};

export const nextSnoozeWakeAt = (notices, history, now = Date.now()) => {
  let soonest = null;
  for (const notice of notices) {
    if (!isSnoozeNotice(notice)) continue;
    if (isNoticeComplete(notice, history[notice.noticeId])) continue;
    const until = resolveSnoozedUntil(notice, history[notice.noticeId]);
    if (until == null || until <= now) continue;
    soonest = soonest == null ? until : Math.min(soonest, until);
  }
  return soonest;
};

const withViewedAt = (existing, extra = {}) => {
  const nextEntry = {
    viewedAt: [...toViewedAtList(existing?.viewedAt), Date.now()]
  };
  const data = extra.data !== undefined ? extra.data : existing?.data;
  if (data !== undefined) nextEntry.data = data;
  if (isFiniteNumber(extra.snoozedUntil)) {
    nextEntry.snoozedUntil = extra.snoozedUntil;
  } else if (!extra.clearSnooze && isFiniteNumber(existing?.snoozedUntil)) {
    nextEntry.snoozedUntil = existing.snoozedUntil;
  }
  return nextEntry;
};

const parseData = (data) => {
  if (typeof data !== 'string') return data;
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

const parseHistory = (raw) => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).map(([noticeId, entry]) => {
        if (!entry || typeof entry !== 'object') {
          return [noticeId, entry];
        }
        return [
          noticeId,
          {
            ...entry,
            viewedAt: toViewedAtList(entry.viewedAt),
            ...(isFiniteNumber(entry.snoozedUntil) ? { snoozedUntil: entry.snoozedUntil } : {}),
            ...(entry.data === undefined ? {} : { data: parseData(entry.data) })
          }
        ];
      })
    );
  } catch {
    return {};
  }
};

const readLocalSnoozes = () => {
  if (typeof localStorage === 'undefined') return {};
  try {
    const parsed = JSON.parse(localStorage.getItem(NOTICEBOARD_SNOOZE_STORAGE_KEY) || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(([, until]) => isFiniteNumber(until))
    );
  } catch {
    return {};
  }
};

const writeLocalSnooze = (noticeId, snoozedUntil) => {
  if (typeof localStorage === 'undefined') return;
  const next = { ...readLocalSnoozes() };
  if (isFiniteNumber(snoozedUntil)) {
    next[noticeId] = snoozedUntil;
  } else {
    delete next[noticeId];
  }
  localStorage.setItem(NOTICEBOARD_SNOOZE_STORAGE_KEY, JSON.stringify(next));
};

const mergeLocalSnoozes = (history) => {
  const local = readLocalSnoozes();
  if (Object.keys(local).length === 0) return history;
  let changed = false;
  const next = { ...history };
  for (const [noticeId, until] of Object.entries(local)) {
    const existing = next[noticeId];
    if (!existing || typeof existing !== 'object') {
      next[noticeId] = { viewedAt: [], snoozedUntil: until };
      changed = true;
      continue;
    }
    if (!isFiniteNumber(existing.snoozedUntil) || until > existing.snoozedUntil) {
      next[noticeId] = { ...existing, snoozedUntil: until };
      changed = true;
    }
  }
  return changed ? next : history;
};

const persistHistory = (identityService, history) => {
  identityService.updateUserSettings({
    [NOTICEBOARD_SETTINGS_KEY]: JSON.stringify(history)
  }).catch(err => {
    console.error('Failed to save noticeboard settings:', err);
  });
};

export const useNoticeboard = () => {
  const [history, setHistory] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClinicId, setSelectedClinicId] = useState(() => SessionStorage.getSelectedClinicId());

  const identityServiceRef = useRef(null);
  if (!identityServiceRef.current) {
    identityServiceRef.current = new IdentityService();
  }

  useEffect(() => {
    identityServiceRef.current.fetchUserSettings()
      .then(allSettings => {
        setHistory(mergeLocalSnoozes(parseHistory(allSettings?.[NOTICEBOARD_SETTINGS_KEY])));
      })
      .catch(err => {
        console.error('Failed to fetch noticeboard settings:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== NOTICEBOARD_SNOOZE_STORAGE_KEY) return;
      setHistory(prev => mergeLocalSnoozes(prev));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const onClinicChanged = (event) => {
      const clinicId = event?.detail?.clinicId;
      setSelectedClinicId(
        typeof clinicId === 'string' && clinicId !== ''
          ? clinicId
          : SessionStorage.getSelectedClinicId()
      );
    };
    window.addEventListener('clinicChanged', onClinicChanged);
    return () => window.removeEventListener('clinicChanged', onClinicChanged);
  }, []);

  const visibleNotices = useMemo(
    () => NOTICES
      .filter(notice => notice.visible && isNoticeForClinic(notice, selectedClinicId))
      .sort((a, b) => b.publishedAt - a.publishedAt),
    [selectedClinicId]
  );

  const isViewed = useCallback(
    (noticeId) => toViewedAtList(history[noticeId]?.viewedAt).length > 0,
    [history]
  );

  const isComplete = useCallback(
    (notice) => isNoticeComplete(notice, history[notice?.noticeId]),
    [history]
  );

  const isDue = useCallback(
    (notice) => isNoticeDue(notice, history[notice?.noticeId]),
    [history]
  );

  const isLoginDue = useCallback(
    (notice) => isLoginPresent(notice, history[notice?.noticeId]),
    [history]
  );

  const isSnoozeWake = useCallback(
    (notice) => isSnoozeWakePresent(notice, history[notice?.noticeId]),
    [history]
  );

  const getData = useCallback(
    (noticeId) => history[noticeId]?.data,
    [history]
  );

  const unseenNotices = useMemo(
    () => visibleNotices
      .filter(notice => isNoticeDue(notice, history[notice.noticeId]))
      .sort((a, b) => a.publishedAt - b.publishedAt),
    [visibleNotices, history]
  );

  const standardNotices = useMemo(
    () => visibleNotices.filter(notice => (
      !isSnoozeNotice(notice) || isNoticeComplete(notice, history[notice.noticeId])
    )),
    [visibleNotices, history]
  );

  const persistentNotices = useMemo(
    () => visibleNotices.filter(notice => (
      isSnoozeNotice(notice) && !isNoticeComplete(notice, history[notice.noticeId])
    )),
    [visibleNotices, history]
  );

  const nextWakeAt = useMemo(
    () => nextSnoozeWakeAt(visibleNotices, history),
    [visibleNotices, history]
  );

  const commitEntry = useCallback((noticeId, extra) => {
    setHistory(prev => {
      const nextEntry = withViewedAt(prev[noticeId], extra);
      const next = { ...prev, [noticeId]: nextEntry };
      persistHistory(identityServiceRef.current, next);
      return next;
    });
  }, []);

  const snooze = useCallback((noticeId, durationMs) => {
    if (!isFiniteNumber(durationMs) || durationMs <= 0) return;
    const snoozedUntil = Date.now() + durationMs;
    writeLocalSnooze(noticeId, snoozedUntil);
    commitEntry(noticeId, { snoozedUntil });
  }, [commitEntry]);

  const dismiss = useCallback((noticeId) => {
    writeLocalSnooze(noticeId);
    commitEntry(noticeId, { clearSnooze: true });
  }, [commitEntry]);

  const saveData = useCallback((noticeId, data) => {
    writeLocalSnooze(noticeId);
    commitEntry(noticeId, { data, clearSnooze: true });
  }, [commitEntry]);

  return {
    visibleNotices,
    standardNotices,
    persistentNotices,
    unseenNotices,
    isViewed,
    isComplete,
    isDue,
    isLoginDue,
    isSnoozeWake,
    getData,
    snooze,
    dismiss,
    saveData,
    nextWakeAt,
    isLoading
  };
};
